/**
 * Curated featured-reviews gallery for the homepage.
 *
 * Renders text testimonials filtered by the active category pill.
 * (The former custom video row was replaced by the Loox reviews
 * carousel, which renders via an app block in the section Liquid.)
 *
 * Data source: assets/featured-reviews.json. URL injected by the
 * section Liquid as window.__FEATURED_REVIEWS_URL.
 */

const CATEGORIES = {
  'great-flavor': 'Great Flavor',
  'sustaining': 'Sustaining',
  'allergen-free': 'Allergen Free',
  'loved-by-kids': 'Loved By Kids',
  'performance': 'Performance',
};
const TEXT_LIMIT = 2;

const starIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 256 256" aria-hidden="true" focusable="false"><path d="M234.5,114.38l-45.1,39.36,13.51,58.6a16,16,0,0,1-23.84,17.34l-51.11-31-51,31a16,16,0,0,1-23.84-17.34L66.61,153.8,21.5,114.38a16,16,0,0,1,9.11-28.06l59.46-5.15,23.21-55.36a15.95,15.95,0,0,1,29.44,0L165.93,81.17l59.46,5.15a16,16,0,0,1,9.11,28.06Z" fill="currentColor"/></svg>`;

async function loadReviews() {
  const url = window.__FEATURED_REVIEWS_URL;
  if (!url) return [];
  const res = await fetch(url, { credentials: 'omit' });
  if (!res.ok) throw new Error(`featured-reviews fetch failed: ${res.status}`);
  const data = await res.json();
  return Array.isArray(data.reviews) ? data.reviews : [];
}

function escapeHtml(str) {
  return String(str == null ? '' : str).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[c]);
}

function starsRow(rating) {
  const filled = Math.max(0, Math.min(5, rating | 0));
  return `<div class="review-stars" role="img" aria-label="${filled} out of 5 stars">${starIcon.repeat(filled)}</div>`;
}

const checkIcon = `<span class="review-text-card__check" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" focusable="false"><path d="M20 6 9 17l-5-5" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"/></svg></span>`;

function provenanceRow(lines) {
  // Entries are {text, check?}. Tolerate a bare string too — mapping over
  // one would otherwise emit a <span> per character.
  const rows = (Array.isArray(lines) ? lines : lines ? [lines] : [])
    .map((l) => (typeof l === 'string' ? { text: l } : l))
    .filter((l) => l && l.text);
  if (rows.length === 0) return '';
  return `<div class="review-text-card__provenance">${rows.map((l) => `
      <span class="review-text-card__provenance-line">${l.check ? checkIcon : ''}${escapeHtml(l.text)}</span>`).join('')}
    </div>`;
}

function renderTextCards(reviews, mount) {
  if (reviews.length === 0) {
    mount.innerHTML = `<li class="ugc-reviews__empty">No text reviews tagged with this filter yet.</li>`;
    return;
  }
  mount.innerHTML = reviews.slice(0, TEXT_LIMIT).map((r) => `
    <li class="review-text-card" data-review-id="${escapeHtml(r.id)}">
      ${starsRow(r.rating)}
      <h3 class="review-text-card__headline">${escapeHtml(r.headline || '')}</h3>
      <p class="review-text-card__body">${escapeHtml(r.body || '')}</p>
      <p class="review-text-card__name">${escapeHtml(r.reviewer_name || '')}</p>
      ${provenanceRow(r.provenance)}
    </li>
  `).join('');
}

function init() {
  const textMount = document.getElementById('featured-reviews-text-mount');
  const filterButtons = Array.from(document.querySelectorAll('.ugc-reviews__filter'));
  if (!textMount || filterButtons.length === 0) return;

  let allReviews = [];
  const initialBtn = filterButtons.find((b) => b.classList.contains('is-active')) || filterButtons[0];
  let activeCategory = initialBtn.dataset.category || null;

  function rerender() {
    const filtered = activeCategory
      ? allReviews.filter((r) => (r.tags || []).includes(activeCategory))
      : allReviews;
    const text = filtered.filter((r) => r.display_mode === 'text');
    renderTextCards(text, textMount);
  }

  filterButtons.forEach((btn) => {
    btn.addEventListener('click', () => {
      activeCategory = btn.dataset.category || null;
      filterButtons.forEach((b) => {
        const isActive = b === btn;
        b.classList.toggle('is-active', isActive);
        b.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      rerender();
    });
  });

  loadReviews()
    .then((reviews) => {
      allReviews = reviews;
      rerender();
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('featured-reviews: load failed', err);
      textMount.innerHTML = `<li class="ugc-reviews__empty">Reviews unavailable.</li>`;
    });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
